/**
 * 訪客追蹤工具
 * 用於追蹤通過 SEO 等方式訪問網站的匿名用戶
 */

import { supabase } from '@/config/supabase'
import { logger } from './logger'

const VISITOR_ID_KEY = 'bentana_visitor_id'
const VISITOR_ID_EXPIRY_DAYS = 365 // 1 年後重新生成

/**
 * 生成或獲取訪客 ID
 */
export function getOrCreateVisitorId(): string {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY)
    const storedDate = localStorage.getItem(`${VISITOR_ID_KEY}_date`)
    
    // 檢查是否過期
    if (storedDate) {
      const storedTime = new Date(storedDate).getTime()
      const now = Date.now()
      const daysDiff = (now - storedTime) / (1000 * 60 * 60 * 24)
      
      if (daysDiff > VISITOR_ID_EXPIRY_DAYS) {
        // 過期，重新生成
        localStorage.removeItem(VISITOR_ID_KEY)
        localStorage.removeItem(`${VISITOR_ID_KEY}_date`)
        visitorId = null
      }
    }
    
    // 如果沒有或過期，生成新的
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem(VISITOR_ID_KEY, visitorId)
      localStorage.setItem(`${VISITOR_ID_KEY}_date`, new Date().toISOString())
    }
    
    return visitorId
  } catch (error) {
    logger.error('獲取訪客 ID 失敗:', error)
    // 降級方案：使用 sessionStorage
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

/**
 * 解析 UTM 參數
 */
function parseUTMParams(): {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
} {
  try {
    const params = new URLSearchParams(window.location.search)
    const result: any = {}
    
    const utm_source = params.get('utm_source')
    const utm_medium = params.get('utm_medium')
    const utm_campaign = params.get('utm_campaign')
    
    if (utm_source) result.utm_source = utm_source
    if (utm_medium) result.utm_medium = utm_medium
    if (utm_campaign) result.utm_campaign = utm_campaign
    
    return result
  } catch (error) {
    return {}
  }
}

/**
 * 檢測設備類型
 */
function detectDevice(): {
  device_type?: string
  browser?: string
  os?: string
} {
  try {
    const ua = navigator.userAgent
    let device_type: string | undefined
    let browser: string | undefined
    let os: string | undefined
    
    // 檢測設備類型
    if (/mobile|android|iphone|ipad/i.test(ua)) {
      device_type = 'mobile'
    } else if (/tablet|ipad/i.test(ua)) {
      device_type = 'tablet'
    } else {
      device_type = 'desktop'
    }
    
    // 檢測瀏覽器
    if (ua.includes('Chrome')) browser = 'Chrome'
    else if (ua.includes('Safari')) browser = 'Safari'
    else if (ua.includes('Firefox')) browser = 'Firefox'
    else if (ua.includes('Edge')) browser = 'Edge'
    
    // 檢測操作系統
    if (ua.includes('Windows')) os = 'Windows'
    else if (ua.includes('Mac')) os = 'Mac'
    else if (ua.includes('Linux')) os = 'Linux'
    else if (ua.includes('Android')) os = 'Android'
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'
    
    return { device_type, browser, os }
  } catch (error) {
    return {}
  }
}

/**
 * 追蹤訪客訪問
 */
export async function trackVisitor(): Promise<void> {
  try {
    const visitorId = getOrCreateVisitorId()
    const referrer = document.referrer || null
    const landingPage = window.location.pathname
    const utmParams = parseUTMParams()
    const deviceInfo = detectDevice()
    
    // 檢查是否是新訪問（檢查是否有記錄）
    const { data: existing, error: selectError } = await supabase
      .from('visitors')
      .select('id, visit_count')
      .eq('visitor_id', visitorId)
      .maybeSingle()
    
    // 如果表不存在（PGRST205 錯誤），靜默失敗
    if (selectError) {
      if (selectError.code === 'PGRST205' || selectError.message?.includes('Could not find the table')) {
        logger.warn('⚠️ visitors 表不存在，跳過訪客追蹤（請執行 創建訪客追蹤表.sql）')
        return
      }
      throw selectError
    }
    
    if (existing) {
      // 更新現有記錄（增加訪問次數）
      const { error } = await supabase
        .from('visitors')
        .update({
          last_visit_at: new Date().toISOString(),
          visit_count: (existing.visit_count || 0) + 1,
          ...utmParams,
          ...deviceInfo,
          updated_at: new Date().toISOString()
        })
        .eq('visitor_id', visitorId)
      
      if (error) {
        // 如果是表不存在錯誤，靜默失敗
        if (error.code === 'PGRST205' || error.message?.includes('Could not find the table')) {
          logger.warn('⚠️ visitors 表不存在，跳過訪客追蹤')
          return
        }
        logger.error('更新訪客記錄失敗:', error)
      }
    } else {
      // 創建新記錄（首次訪問）
      const { error } = await supabase
        .from('visitors')
        .insert({
          visitor_id: visitorId,
          referrer_url: referrer,
          landing_page: landingPage,
          user_agent: navigator.userAgent,
          ...utmParams,
          ...deviceInfo,
          conversion_status: 'visitor'
        })
      
      if (error) {
        // 如果是表不存在錯誤，靜默失敗
        if (error.code === 'PGRST205' || error.message?.includes('Could not find the table')) {
          logger.warn('⚠️ visitors 表不存在，跳過訪客追蹤（請執行 創建訪客追蹤表.sql）')
          return
        }
        logger.error('創建訪客記錄失敗:', error)
      } else {
        logger.log('✅ 訪客記錄已創建:', visitorId)
      }
    }
  } catch (error: any) {
    // 如果是表不存在錯誤，靜默失敗
    if (error?.code === 'PGRST205' || error?.message?.includes('Could not find the table')) {
      logger.warn('⚠️ visitors 表不存在，跳過訪客追蹤')
      return
    }
    logger.error('追蹤訪客失敗:', error)
    // 不阻止頁面載入，靜默失敗
  }
}

/**
 * 當用戶連接錢包時，關聯訪客記錄
 */
export async function linkVisitorToWallet(walletAddress: string): Promise<boolean> {
  try {
    const visitorId = getOrCreateVisitorId()
    
    logger.log('🔗 關聯訪客到錢包...')
    logger.log(`   - 訪客 ID: ${visitorId}`)
    logger.log(`   - 錢包地址: ${walletAddress}`)
    
    // 調用資料庫函數
    const { data, error: rpcError } = await supabase.rpc('link_visitor_to_wallet', {
      p_visitor_id: visitorId,
      p_wallet_address: walletAddress.toLowerCase()
    })
    
    // 如果函數不存在或表不存在，靜默失敗
    if (rpcError) {
      if (rpcError.code === '42883' || rpcError.message?.includes('function') || 
          rpcError.code === 'PGRST205' || rpcError.message?.includes('Could not find the table')) {
        logger.warn('⚠️ visitors 表或函數不存在，跳過訪客關聯（請執行 創建訪客追蹤表.sql）')
        return false
      }
      
      logger.error('關聯訪客到錢包失敗:', rpcError)
      
      // 降級方案：手動更新
      const { error: updateError } = await supabase
        .from('visitors')
        .update({
          wallet_address: walletAddress.toLowerCase(),
          conversion_status: 'connected',
          updated_at: new Date().toISOString()
        })
        .eq('visitor_id', visitorId)
      
      if (updateError) {
        // 如果是表不存在錯誤，靜默失敗
        if (updateError.code === 'PGRST205' || updateError.message?.includes('Could not find the table')) {
          logger.warn('⚠️ visitors 表不存在，跳過訪客關聯')
          return false
        }
        logger.error('手動關聯失敗:', updateError)
        return false
      }
      
      logger.log('✅ 已手動關聯訪客到錢包')
      return true
    }
    
    if (data && (data as any).success) {
      logger.log('✅ 訪客已成功關聯到錢包')
      return true
    }
    
    return false
  } catch (error: any) {
    // 如果是表不存在錯誤，靜默失敗
    if (error?.code === 'PGRST205' || error?.code === '42883' || 
        error?.message?.includes('Could not find the table') || 
        error?.message?.includes('function')) {
      logger.warn('⚠️ visitors 表或函數不存在，跳過訪客關聯')
      return false
    }
    logger.error('關聯訪客異常:', error)
    return false
  }
}

/**
 * 獲取當前訪客 ID（用於調試）
 */
export function getCurrentVisitorId(): string | null {
  try {
    return localStorage.getItem(VISITOR_ID_KEY)
  } catch {
    return null
  }
}

